/**
 * Detecting a relay lane that died while the tab sat idle.
 *
 * An Azure Web PubSub `sendToGroup` succeeds even when nobody is subscribed to
 * the group, so a dispatch to a reclaimed lane looks identical to a delivered
 * one: the transport acks, and the host simply never echoes the action back.
 * Nothing in the WebSocket closes, because the socket terminates at Azure
 * rather than at the daemon.
 *
 * Two independent reapers can take the lane away while the client is idle:
 *
 * - copilotd reclaims a per-client relay lane after its keep-alive window (15
 *   minutes by default) and leaves the `to-host` group.
 * - Mission Control asks for the sandbox compute to stop once the session goes
 *   idle, which lands on a delay measured in minutes. That kills the daemon's
 *   connection outright, and the sandbox itself also auto-suspends on a longer
 *   platform timer.
 *
 * We deliberately do not keep the lane warm with periodic pings. An idle
 * browser tab must be allowed to let its sandbox suspend, otherwise a
 * forgotten tab would pin compute open indefinitely. Instead the client stays
 * silent while idle and checks liveness on demand, immediately before it needs
 * the lane again.
 */

/**
 * Fallback keep-alive window used when the host does not advertise one. Matches
 * copilotd's `DEFAULT_RELAY_IDLE_TIMEOUT_SECS`. The host documents that this
 * default may shrink in future releases, so it is only ever used as an upper
 * bound for "definitely dead", never as a promise that the lane is alive.
 */
export const DEFAULT_KEEPALIVE_TIMEOUT_MS = 15 * 60 * 1000

/**
 * Idle time after which a dispatch is preceded by a liveness probe. Well below
 * any reaper's window, because compute can stop earlier than the host's
 * keep-alive window and the probe costs a single round trip on a path where the
 * user is already waiting on the network.
 */
export const LIVENESS_PROBE_IDLE_MS = 60 * 1000

/** How long to wait for a `ping` response before declaring the lane dead. */
export const LIVENESS_PROBE_TIMEOUT_MS = 10 * 1000

/**
 * What to do with a lane before dispatching on it.
 *
 * - `skip`: a recent response from the host proves the lane is live.
 * - `probe`: idle long enough to be uncertain; confirm with a `ping`.
 * - `reconnect`: the last outbound attempt is older than the keep-alive window,
 *   so the host has certainly reclaimed the lane and a probe would only add
 *   latency.
 */
export type LivenessDecision = 'skip' | 'probe' | 'reconnect'

/**
 * Read the host's advertised keep-alive window from a handshake result.
 *
 * copilotd reports this as `_meta['copilot.keepAliveTimeoutMs']` on both
 * `initialize` and `reconnect` results. Returns `undefined` when absent or not
 * a usable positive number, so callers fall back to the documented default.
 */
export function readKeepAliveTimeoutMs(handshake: {_meta?: Record<string, unknown>} | undefined): number | undefined {
  const raw = handshake?._meta?.['copilot.keepAliveTimeoutMs']
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return undefined
  return raw
}

export function decideLiveness({
  lastOutboundAtMs,
  lastConfirmedAtMs,
  nowMs,
  keepAliveTimeoutMs = DEFAULT_KEEPALIVE_TIMEOUT_MS,
  probeIdleMs = LIVENESS_PROBE_IDLE_MS,
}: {
  lastOutboundAtMs: number | undefined
  lastConfirmedAtMs: number | undefined
  nowMs: number
  keepAliveTimeoutMs?: number
  probeIdleMs?: number
}): LivenessDecision {
  // No host response yet means the connection is either brand new or has only
  // attempted fire-and-forget notifications. Only the latter needs a probe.
  if (lastConfirmedAtMs === undefined) return lastOutboundAtMs === undefined ? 'skip' : 'probe'
  // A clock adjustment can push `nowMs` behind the recorded timestamp. Treat
  // that as "no measurable idle time" rather than inferring a huge gap.
  const confirmedIdleMs = Math.max(0, nowMs - lastConfirmedAtMs)
  if (confirmedIdleMs < probeIdleMs) return 'skip'

  // An attempted notification resets the host's reaper only if it was actually
  // delivered. It can therefore rule out a known-dead lane only long enough to
  // justify probing; it can never itself prove the lane is live.
  const outboundIdleMs = Math.max(0, nowMs - (lastOutboundAtMs ?? lastConfirmedAtMs))
  return outboundIdleMs >= keepAliveTimeoutMs ? 'reconnect' : 'probe'
}
